import React, { useState, useRef, useCallback } from 'react'
import { Box, Popper, Paper, ClickAwayListener, List, ListItemButton, ListItemText, useTheme } from '@mui/material'
import { TextField } from 'components/@extended/TextField'

export interface VariableSuggestion {
  label: string
  value: string
  category: 'actual' | 'target'
  componentLabel: string
}

interface FormulaTextFieldProps {
  value: string
  onChange: (newValue: string) => void
  onCommit?: () => void
  suggestions: VariableSuggestion[]
  placeholder?: string
  autoFocus?: boolean
}

const DELIMITERS = /[+\-*/()>=<!,;]/

function getTokenBounds(text: string, cursorPos: number): { start: number; end: number } {
  let start = cursorPos
  let end = cursorPos

  while (start > 0 && !DELIMITERS.test(text[start - 1])) {
    start--
  }
  while (end < text.length && !DELIMITERS.test(text[end])) {
    end++
  }

  // Trim leading/trailing spaces from the token bounds
  while (start < end && text[start] === ' ') start++
  while (end > start && text[end - 1] === ' ') end--

  return { start, end }
}

const FormulaTextField: React.FC<FormulaTextFieldProps> = ({
  value,
  onChange,
  onCommit,
  suggestions,
  placeholder,
  autoFocus,
}) => {
  const theme = useTheme()
  const [popperOpen, setPopperOpen] = useState(false)
  const [filteredSuggestions, setFilteredSuggestions] = useState<VariableSuggestion[]>([])
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [wordBounds, setWordBounds] = useState<{ start: number; end: number }>({ start: 0, end: 0 })

  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  const updateSuggestions = useCallback(
    (text: string, cursorPos: number) => {
      const bounds = getTokenBounds(text, cursorPos)
      const token = text.slice(bounds.start, bounds.end).trim()

      if (token.length < 2) {
        setPopperOpen(false)
        return
      }

      const lowerToken = token.toLowerCase()
      const matches = suggestions.filter((s) => s.label.toLowerCase().startsWith(lowerToken))

      if (matches.length > 0) {
        setFilteredSuggestions(matches)
        setHighlightedIndex(0)
        setWordBounds(bounds)
        setPopperOpen(true)
      } else {
        setPopperOpen(false)
      }
    },
    [suggestions]
  )

  const insertSuggestion = useCallback(
    (suggestion: VariableSuggestion) => {
      const before = value.slice(0, wordBounds.start)
      const after = value.slice(wordBounds.end)
      const newValue = before + suggestion.value + ' ' + after
      const newCursorPos = before.length + suggestion.value.length + 1

      onChange(newValue)
      setPopperOpen(false)

      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
          inputRef.current.focus()
        }
      })
    },
    [value, wordBounds, onChange]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value
    onChange(newValue)

    const cursorPos = e.target.selectionStart ?? newValue.length
    updateSuggestions(newValue, cursorPos)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (popperOpen && filteredSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev + 1) % filteredSuggestions.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertSuggestion(filteredSuggestions[highlightedIndex])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setPopperOpen(false)
        return
      }
    } else {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        onCommit?.()
      }
    }
  }

  const handleClickAway = () => {
    setPopperOpen(false)
  }

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        size="small"
        multiline
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        variant="outlined"
        pill
        shrink
        autoFocus={autoFocus}
        inputRef={inputRef}
      />
      <Popper
        open={popperOpen && filteredSuggestions.length > 0}
        anchorEl={wrapperRef.current}
        placement="bottom-start"
        disablePortal={false}
        sx={{ zIndex: 1301 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            elevation={8}
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              mt: 0.5,
              minWidth: 220,
            }}
          >
            <List dense disablePadding>
              {filteredSuggestions.map((suggestion, idx) => (
                <ListItemButton
                  key={suggestion.label}
                  selected={idx === highlightedIndex}
                  onClick={() => insertSuggestion(suggestion)}
                  sx={{
                    py: 0.5,
                    px: 1.5,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.lighter,
                    },
                  }}
                >
                  <ListItemText
                    primary={suggestion.label}
                    secondary={suggestion.componentLabel}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  )
}

export default FormulaTextField
